<template>
<div class="bot-chat-layout">
 <PageTitle :title="t('bot')" />
  <div class="wrapper">
    <AppBackButton link="/bot" :linkMode="true" class="ma-2">
      <AppIcon icon="back-arrow-icon" />
      <span>{{ t('chat.back') }}</span>
    </AppBackButton>

    <StateWrapper
      :contentObject="chat"
      :loadingStatus="loadingStatus"
      :checkLogin="true"
    >
      <UserContentWrapper
        :title="chat.title"
        @back="router.push({ path: '/bot' })"
        class="bot-chat-layout__content"
      >
        <v-card variant="outlined" class="  bot-chat-layout__chat-main" id="chat_window">
          <!-- Empty chat state -->
          <v-card class="ma-2" variant="outlined" v-if="chat.history.length === 0 && !sendDisabled">
            <p class="pa-4 ma-2">{{ t('chat.empty') }}</p>
          </v-card>

          <!-- Chat messages -->
          <div v-else>
            <div
              v-for="msg in chat.history"
              :key="msg.id"
              :class="{ 'd-flex flex-row-reverse': msg.role === 'USER' }"
            >
              <v-card
                :color="msg.role === 'USER' ? 'indigo-lighten-3' : 'pink-lighten-3'"
                variant="outlined"
                style="height:auto;white-space: normal;"
                class="pa-2 ma-2 bot-chat-layout__message-card"
              >
                <template v-slot:title>
                  <v-row v-if="msg.role === 'USER'">
                      <div class="d-flex align-center">
                           <div class="n-avatar ma-4">
                               <AppImage v-if="user.personAvatar" :image="`https://navigator-backend.eurekacenter.ru/api/download/avatar/user/${user.personAvatar}`" />
                               <div v-else class="n-avatar__pseudo-avatar">
                                   <AppIcon icon="bot-icon" />
                               </div>
                           </div>
                          <h3 class="mt-4 mb-4">
                            {{ t('chat.you') }}
                          </h3>
                      </div>
                  </v-row>
                  <v-row v-else >
                      <div class="d-flex align-center">
                        <div  class="n-avatar ma-4">
                            <div class="n-avatar__pseudo-avatar">
                                <AppIcon icon="bot-icon" />
                            </div>
                        </div>
                        <h3 class="mt-4 mb-4">
                            {{ t('bot') }}
                        </h3>
                      </div>
                  </v-row>
                </template>
                <p class="pa-4 ma-2" v-if="msg.content">
                  <VMarkdownView :mode="mode" :content="msg.content"></VMarkdownView>
                </p>
                <sub v-if="msg.createdAt" class="ml-2">
                  {{ formatDate(msg.createdAt) }}
                </sub>
              </v-card>
            </div>

            <!-- Pending user message -->
            <div v-if="sendDisabled">
              <div class="d-flex flex-row-reverse">
                <v-card
                  :color="'indigo-lighten-1'"
                  :title="t('chat.you')"
                  variant="outlined"
                  style="height:auto;white-space: normal;"
                  class="pa-4 ma-2 bot-chat-layout__message-card"
                >
                  <p class="pa-4 ma-2" v-if="messageInput && messageInput.length > 0">
                    <VMarkdownView :mode="mode" :content="capitalizeFirstLetter(messageInput)"></VMarkdownView>
                  </p>
                </v-card>
              </div>
            </div>

            <!-- Pending bot response -->
            <div v-if="sendDisabled">
              <div class="d-flex">
                <v-card
                  :color="'pink-lighten-1'"
                  :title="t('bot')"
                  variant="outlined"
                  style="height:auto;white-space: normal;"
                  class="pa-4 ma-2 bot-chat-layout__message-card"
                >
                  <v-row
                    align-content="center"
                    class="fill-height"
                    justify="center"
                  >
                    <v-col
                      class="text-center"
                      cols="12"
                    >
                      {{ t('chat.thinking') }}
                    </v-col>
                    <v-col cols="12">
                      <v-progress-linear
                        color="deep-pink-accent-4"
                        height="6"
                        indeterminate
                        rounded
                      >
                      </v-progress-linear>
                    </v-col>
                    <v-col cols="12">
                      <VMarkdownView class="ma-2 pa-2" :mode="mode" :content="fullText"></VMarkdownView>
                    </v-col>
                  </v-row>
                </v-card>
              </div>
            </div>
          </div>
        </v-card>

        <!-- Message input -->

          <v-textarea
            v-model="messageInput"
            dense
            variant="outlined"
            :placeholder="t('chat.type_message')"
            :hint="t('chat.type_message_hint')"
            :disabled="sendDisabled"
            rows="6"
            auto-grow
            clearable
            @keyup.enter="sendMessage"
          />
          <v-btn
            variant="outlined"
            class="text-indigo-lighten-1 mt-2"
            style="width: 100%;"
            @click="sendMessage"
            :disabled="sendDisabled || !messageInput.trim()"
          >
            {{ t('send') }}
          </v-btn>

      </UserContentWrapper>
    </StateWrapper>

</div>
</div>
</template>

<script setup>

import { ref, onMounted, watch, onUpdated } from 'vue';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { t } from '@/utils/translate';
import { capitalizeFirstLetter } from '@/utils/common';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { usePopupStore } from '@/store/popupStore';
import { customAxios } from '@/service/customAxios';
import { botApiUrl } from '@/service/user.service';
import { customStorage } from '@/service/customStorage';
import AppImage from '@/elements/AppImage.vue';
import AppIcon from '@/elements/AppIcon.vue';
import UserContentWrapper from '@/elements/UserContentWrapper.vue';
import StateWrapper from '@/components/StateWrapper.vue';
import { VMarkdownView } from 'vue3-markdown';
import PageTitle from '@/elements/PageTitle.vue';
import AppBackButton from "@/elements/AppBackButton.vue";

// Store references
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const { user } = storeToRefs(userStore);
const popupStore = usePopupStore();
const { showPopup } = popupStore;

// Ensure user is available
if (!user.value) {
  // This should be handled by the LoginPlease component, but let's add a safety check
  console.warn('User not available in chat layout');
}

// Reactive variables
const chat = ref();
const loadingStatus = ref('loading');
const mode = ref('light');
const messageInput = ref('');
const sendDisabled = ref(false);
const fullText = ref('');

// Computed properties
const isUserMessage = (msg) => msg.role === 'USER';

// Methods
function formatDate(dateString) {
  const formatter = new Intl.DateTimeFormat("ru", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
  return capitalizeFirstLetter(formatter.format(Date.parse(dateString)));
}

function dropDown() {
  const chatContainer = document.getElementById("chat_window");
  if (chatContainer) {
    chatContainer.scrollTop = chatContainer.scrollHeight;
  }
}

// Watchers
watch(() => fullText.value, () => {
  dropDown();
}, { deep: true });

watch(() => chat.value, () => {
  dropDown();
}, { deep: true });

watch(() => messageInput.value, (newVal) => {
  messageInput.value = capitalizeFirstLetter(newVal);
}, { deep: true });

onUpdated(dropDown);

async function sendMessage() {
  if (!messageInput.value.trim()) return;

  sendDisabled.value = true;
  fullText.value = '';

  try {
    const url = `${botApiUrl}/chat-stream/${route.params.id}?userPrompt=${encodeURIComponent(messageInput.value)}&token=${encodeURIComponent(customStorage.get("user-token"))}`;
    const eventSource = new EventSource(url);

    // Clean up previous event source if exists
    eventSource.onmessage = function(event) {
      const data = JSON.parse(event.data);

      if (data && data.statusCode && data.statusCode.includes('CREATED')) {
        eventSource.close();
        getMyChat();
        sendDisabled.value = false;
        messageInput.value = '';
      } else if (data && data.statusCode && data.statusCode === "SERVICE_UNAVAILABLE") {
        showPopup(data.body.message, "error");
        eventSource.close();
        dropDown();
        sendDisabled.value = false;
      } else {
        const token = data.text;
        fullText.value += token;
        dropDown();
      }
    };

    eventSource.onerror = function(event) {
      if (event.currentTarget.readyState === EventSource.CLOSED) {
        console.log("SSE connection was closed gracefully.");
        eventSource.close();
        getMyChat();
        sendDisabled.value = false;
        messageInput.value = '';
        setTimeout(() => {
          dropDown();
        }, 500);
      } else {
        console.error("SSE error:", event);
        eventSource.close();
        getMyChat();
        sendDisabled.value = false;
        messageInput.value = '';
      }
    };
  } catch (e) {
    console.error('Error sending message:', e);
    sendDisabled.value = false;
    showPopup('Ошибка отправки сообщения', 'error');
  }
}

async function getMyChat() {
  if (!route.params.id) {
    return;
  }

  try {
    const r = await customAxios.getExternal(`${botApiUrl}/api/chat/${route.params.id}`);
    if (r.status === 200) {
      loadingStatus.value = "loaded";
      chat.value = r.data;
      setTimeout(() => {
        dropDown();
      }, 500);
    }
  } catch (e) {
    loadingStatus.value = "loaded";
    if (e.code === "ERR_NETWORK") {
      showPopup("Чат временно не доступен! Попробуйте обновить страницу, либо зайти позже.", "error");
    } else if (e.response && e.response.data && e.response.data.message) {
      showPopup(e.response.data.message, "error");
    } else {
      showPopup("Произошла ошибка при загрузке чата", "error");
    }
  } finally {
    dropDown();
  }
}

// Lifecycle
onMounted(async () => {
  await getMyChat();
});
</script>

<style lang="scss">
.bot-chat-layout {
  height: 100%;
  min-width: 360px;

  &__wrapper {
    @include page;
  }

  &__content {
    .v-card {
      margin-bottom: 10px;
    }
  }

  &__chat-main {
    height: 600px;
    overflow-y: auto;
    min-height: 400px;
  }



  &__message-card {
    max-width: 90%;

    &.v-card {
      margin-bottom: 10px;
    }
  }

  .markdown-body {
    margin: 0;
    color: #333;
    font-size: 16px;
    line-height: 20px;
    font-family: "Comfortaa", sans-serif;
    font-weight: 700;
  }

  .n-avatar {
    width: 60px;
    height: 60px;

    &__pseudo-avatar {
      width: 100%;
      height: 100%;
      display: flex;

      justify-content: center;
    }
  }
}
</style>




